import { key } from './snippet.js';
import { trustOptOutGroups } from './constants.js';

/* One non-identifying boolean per tab ("this visitor was already offered a bubble"), so a nudge does not
 * pop again on every page of a visit. Deliberately not part of the analytics consent gate: it stores no
 * id, survives nothing beyond the tab, and exists only to stop the UI from repeating itself. Blocked
 * storage degrades to the in-memory flag below, which still caps repeats within one page load.
 * The contractor can switch the widget to "every page", and then nothing is remembered beyond the
 * current page at all. */
const nudgeSeenKey = 'feedbax_nudge:' + key;
let nudgesSeenInMemory = false;

/**
 * Forgets that the bubbles were shown, for this page view only — the session mark stays where it is.
 * That is the difference to resetNudgesSeen below, and it is deliberate: a widget set to "every page"
 * owes the next route its bubbles again, but a visitor who dismissed them for the visit keeps that.
 */
export function forgetNudgesSeenThisPage() {
    nudgesSeenInMemory = false;
}

export function resetNudgesSeen() {
    nudgesSeenInMemory = false;
    try {
        sessionStorage.removeItem(nudgeSeenKey);
    } catch { /* nothing stored, nothing to clear */ }
}

export function nudgesAlreadySeen(everyPage) {
    if (nudgesSeenInMemory) {
        return true;
    }
    if (everyPage) {
        return false; // Every page is a fresh chance; only this page load caps itself.
    }
    try {
        return sessionStorage.getItem(nudgeSeenKey) === '1';
    } catch {
        return false; // Storage blocked — the in-memory flag is all we have.
    }
}

export function markNudgesSeen(everyPage) {
    nudgesSeenInMemory = true;

    if (everyPage) {
        return; // Nothing to carry into the next page.
    }
    try {
        sessionStorage.setItem(nudgeSeenKey, '1');
    } catch { /* storage blocked — in-memory only */ }
}

/* Whether the visitor wrote in the chat during this tab's visit, reported by the widget frame. A visitor who
 * opened the chat and closed it again without writing gets the bubbles back (Armin 2026-10-21); one who wrote
 * has had the conversation the bubbles were asking for. Per tab like the mark above, so it carries across the
 * pages of a visit: a visitor who chatted on the first page is not teased again on the third. One
 * non-identifying boolean, outside the analytics consent gate for the same reason. */
const visitorWroteKey = 'feedbax_wrote:' + key;
let visitorWroteInMemory = false;

export function visitorWrote() {
    if (visitorWroteInMemory) {
        return true;
    }
    try {
        return sessionStorage.getItem(visitorWroteKey) === '1';
    } catch {
        return false; // Storage blocked — the in-memory flag is all we have.
    }
}

export function markVisitorWrote() {
    visitorWroteInMemory = true;
    try {
        sessionStorage.setItem(visitorWroteKey, '1');
    } catch { /* storage blocked — in-memory only */ }
}

/* The same idea for the launcher's trust surface, under a key of its own: the two are different offers
 * and a visitor who was shown one was not shown the other. Same reasoning throughout — one
 * non-identifying boolean per tab, nothing that survives it, outside the analytics consent gate because
 * it stores no id and travels nowhere. The contractor can switch the surface to "every page", and then
 * nothing is remembered at all. */
const trustSeenKey = 'feedbax_trust:' + key;
let trustSeenInMemory = false;

export function resetTrustSeen() {
    trustSeenInMemory = false;
    try {
        sessionStorage.removeItem(trustSeenKey);
    } catch { /* nothing stored, nothing to clear */ }
}

export function trustAlreadySeen(everyPage) {
    if (everyPage) {
        return false; // Every page is a fresh look; nothing is remembered across them.
    }
    if (trustSeenInMemory) {
        return true;
    }
    try {
        return sessionStorage.getItem(trustSeenKey) === '1';
    } catch {
        return false; // Storage blocked — the in-memory flag is all we have.
    }
}

export function markTrustSeen(everyPage) {
    trustSeenInMemory = true;

    if (everyPage) {
        return; // Nothing to carry into the next page.
    }
    try {
        sessionStorage.setItem(trustSeenKey, '1');
    } catch { /* storage blocked — in-memory only */ }
}

/* The visitor's standing "no thanks", offered right where they dismissed a bubble. localStorage rather
 * than sessionStorage: dismissing a bubble means "not now", opting out means "not again", and a decision
 * that has to be repeated on every visit is not one. Still one non-identifying boolean per widget — it
 * stores no id and travels nowhere, so it stays outside the analytics consent gate like its sibling
 * above. A visitor who never opts out is unaffected by any of this. */
const nudgeOptOutKey = 'feedbax_nudge_optout:' + key;
let nudgesOptedOutInMemory = false;

export function nudgesOptedOut() {
    if (nudgesOptedOutInMemory) {
        return true;
    }
    try {
        return localStorage.getItem(nudgeOptOutKey) === '1';
    } catch {
        return false; // Storage blocked — the in-memory flag still holds for this page load.
    }
}

export function optOutOfNudges() {
    nudgesOptedOutInMemory = true;
    try {
        localStorage.setItem(nudgeOptOutKey, '1');
    } catch { /* storage blocked — the choice holds for this page load at least */ }
}

/**
 * Takes the standing "not again" back. The counterpart to the chip above, and the reason it may exist at
 * all: a one-tap choice that lives on a foreign website for good, with nothing anywhere that undoes it,
 * is a door that only opens one way — the widget's own header menu is where the visitor finds the handle
 * (see WidgetHiddenSignalsPanel), exactly as it is for the launcher's trust corner.
 * @returns {boolean} Whether anything was actually switched back on
 */
export function showNudges() {
    const wasOptedOut = nudgesOptedOut();

    nudgesOptedOutInMemory = false;
    try {
        localStorage.removeItem(nudgeOptOutKey);
    } catch { /* nothing stored, nothing to clear */ }

    return wasOptedOut;
}

/* The same standing "no thanks" for the launcher's trust corner, and in the same storage for the same
 * reason: dismissing a card means "not now", opting out means "not again". A LIST rather than a boolean,
 * because the corner shows three different things and a visitor who does not care to read strangers'
 * quotes may well want to see what the provider built — and because this one is reversible from inside
 * the widget (the header menu's way back), where taking one group off the list must leave the others
 * where the visitor put them. Still non-identifying: three fixed words, no id, and nothing travels. */
const trustOptOutKey = 'feedbax_trust_optout:' + key;
const trustOptOutInMemory = new Set();
let trustOptOutRead = false;

/**
 * The groups this visitor has switched off, read from storage once per page load and kept in memory
 * afterwards — the set is asked on every deck build and every restore, and a blocked storage must not
 * cost a throw each time.
 * @returns {Set<string>} Live set; the mutators below write it back to storage.
 */
export function hiddenTrustGroups() {
    if (trustOptOutRead) {
        return trustOptOutInMemory;
    }

    trustOptOutRead = true;

    try {
        const stored = (localStorage.getItem(trustOptOutKey) || '').split(',');
        for (let i = 0; i < stored.length; i++) {
            // Only the three known words survive the read: the value is the visitor's own storage and
            // anything else in it is somebody's leftover, not an instruction.
            if (trustOptOutGroups.indexOf(stored[i]) >= 0) {
                trustOptOutInMemory.add(stored[i]);
            }
        }
    } catch { /* storage blocked — the in-memory set is all there is, and it holds for this page load */ }

    return trustOptOutInMemory;
}

/** Writes the set back in the constants' own order, or clears the key once nothing is hidden. */
function persistHiddenTrustGroups() {
    const groups = trustOptOutGroups.filter(function (group) {
        return trustOptOutInMemory.has(group);
    });

    try {
        if (groups.length > 0) {
            localStorage.setItem(trustOptOutKey, groups.join(','));
        } else {
            localStorage.removeItem(trustOptOutKey);
        }
    } catch { /* storage blocked — the choice holds for this page load at least */ }
}

export function hideTrustGroup(group) {
    hiddenTrustGroups().add(group);
    persistHiddenTrustGroups();
}

/**
 * Takes hidden groups back — the way out of the opt-out, asked for from inside the widget.
 * @param {string} group - One of the three, or 'all' for every one of them at once
 * @returns {boolean} Whether anything actually changed
 */
export function showTrustGroups(group) {
    const hidden = hiddenTrustGroups();
    const wanted = group === 'all' ? trustOptOutGroups : [group];
    let changed = false;

    for (let i = 0; i < wanted.length; i++) {
        if (hidden.delete(wanted[i])) {
            changed = true;
        }
    }

    if (changed) {
        persistHiddenTrustGroups();
    }

    return changed;
}

/* The campaign frequency memory: one non-identifying flag per campaign, in the storage its frequency
 * names — localStorage for "once per visitor", sessionStorage for "once per session", the in-memory
 * set when storage is blocked (which still caps repeats within one page load). Like the nudge flags
 * above it stays outside the analytics consent gate: it stores no id and travels nowhere. Marked when
 * a campaign is actually on screen, never when it was merely scheduled. */
const announcementSeenPrefix = 'feedbax_announcement:' + key + ':';
const announcementsSeenInMemory = new Set();

export function announcementSeen(announcement) {
    if (announcementsSeenInMemory.has(announcement.id)) {
        return true;
    }
    try {
        const store = announcement.freq === 'session' ? sessionStorage : localStorage;
        return store.getItem(announcementSeenPrefix + announcement.id) === '1';
    } catch {
        return false; // Storage blocked — the in-memory set still holds for this page load.
    }
}

export function markAnnouncementSeen(announcement) {
    announcementsSeenInMemory.add(announcement.id);
    try {
        const store = announcement.freq === 'session' ? sessionStorage : localStorage;
        store.setItem(announcementSeenPrefix + announcement.id, '1');
    } catch { /* storage blocked — in-memory only */ }
}

/* The launcher's unread card. Two values, both in localStorage because a reply that waits for a visitor
 * who comes back tomorrow is exactly the case the card is for.
 *
 * The peek token is what the widget frame hands down once the visitor has a conversation: sealed ids that
 * answer one question on the next page load, before any frame exists ("is a team reply waiting, and from
 * whom?"). It opens no transcript. It exists only because the visitor started a conversation, so it is part
 * of the service they asked for, not tracking, and like the flags above it stays outside the analytics
 * consent gate.
 *
 * The dismissal remembers which reply the visitor closed the card over (conversation and time), so the card
 * stays away for that reply and comes back for the next one. The red dot ignores it: a dismissed card is
 * still an unread reply, as in every messenger. */
const inboxPeekKey = 'feedbax_inbox:' + key;
const inboxDismissedKey = 'feedbax_inbox_dismissed:' + key;
let inboxDismissedInMemory = '';

export function readInboxPeek() {
    try {
        return localStorage.getItem(inboxPeekKey) || '';
    } catch {
        return ''; // Storage blocked — the card waits for the frame of this page load instead.
    }
}

export function writeInboxPeek(peek) {
    try {
        if (peek) {
            localStorage.setItem(inboxPeekKey, peek);
        } else {
            localStorage.removeItem(inboxPeekKey);
        }
    } catch { /* storage blocked — the next page load simply has no card */ }
}

export function inboxReplyDismissed(mark) {
    if (inboxDismissedInMemory === mark) {
        return true;
    }
    try {
        return localStorage.getItem(inboxDismissedKey) === mark;
    } catch {
        return false;
    }
}

export function dismissInboxReply(mark) {
    inboxDismissedInMemory = mark;
    try {
        localStorage.setItem(inboxDismissedKey, mark);
    } catch { /* storage blocked — the card stays away for this page load at least */ }
}
